import { useEffect, useMemo, useState } from "react";
import type { DownloadAvailability, DownloadItem } from "../types";
import { api, ApiError, fileDownloadUrl } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import type { UiStrings } from "../i18n";

type Platform = "all" | "windows" | "linux";

function categoryLabel(t: UiStrings, key: string): string {
  return (
    t.dlCategories[key] ?? (key ? key[0].toUpperCase() + key.slice(1) : "Other")
  );
}

function DownloadCard({ item }: { item: DownloadItem }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="dl-card">
      <div className="dl-card-head">
        <div className="dl-card-title">
          <h4>{item.name}</h4>
          <div className="dl-badges">
            {item.isLatest && <span className="badge badge-accent">Latest</span>}
            {item.isBeta && <span className="badge badge-warn">Beta</span>}
            {item.platform && item.platform !== "all" && (
              <span className="badge">{item.platform}</span>
            )}
          </div>
        </div>
        <a
          className="btn btn-primary btn-sm"
          href={fileDownloadUrl(item.filename)}
          download={item.filename}
        >
          {t.dlDownload}
        </a>
      </div>

      {item.description && <p className="dl-desc muted">{item.description}</p>}

      <div className="dl-meta">
        {item.version && <span>v{item.version}</span>}
        {item.fileSize && <span>{item.fileSize}</span>}
        {item.releaseDate && <span>{item.releaseDate}</span>}
        <span className="dl-file" title={item.filename}>
          {item.filename}
        </span>
      </div>

      {(item.requirements.length > 0 || item.changelog.length > 0) && (
        <button className="dl-toggle" onClick={() => setOpen((v) => !v)}>
          {open ? t.dlHideDetails : t.dlShowDetails}
        </button>
      )}

      {open && (
        <div className="dl-details">
          {item.requirements.length > 0 && (
            <div>
              <h5>{t.dlRequirements}</h5>
              <ul>
                {item.requirements.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {item.changelog.length > 0 && (
            <div>
              <h5>{t.dlWhatsNew}</h5>
              <ul>
                {item.changelog.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DownloadsPage() {
  const { session } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState<DownloadItem[] | null>(null);
  const [availability, setAvailability] = useState<DownloadAvailability | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform>("all");

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setItems(null);
    Promise.all([api.getDownloads(), api.getAvailability().catch(() => null)])
      .then(([dls, avail]) => {
        if (cancelled) return;
        setItems(dls);
        setAvailability(avail);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : t.dlLoadError);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return platform === "all"
      ? items
      : items.filter((d) => d.platform === platform || d.platform === "all");
  }, [items, platform]);

  const grouped = useMemo(() => {
    const map = new Map<string, DownloadItem[]>();
    for (const d of filtered) {
      const key = d.category || "other";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div className="page downloads">
      <div className="page-head">
        <h1>{t.dlTitle}</h1>
        <p className="muted">
          {t.dlSubtitle(
            availability ? t.dlSubtitleModeSuffix(availability.deploymentMode) : ""
          )}
        </p>
      </div>

      <div className="notice notice-ok">
        {t.dlSignedInNotice(session?.email ?? "")}
        {t.dlVerifyNotice}
      </div>

      <div className="dl-toolbar">
        <div className="seg">
          {(["all", "windows", "linux"] as Platform[]).map((p) => (
            <button
              key={p}
              className={`seg-btn${platform === p ? " active" : ""}`}
              onClick={() => setPlatform(p)}
            >
              {p === "all" ? t.dlPlatformAll : p}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="notice notice-err">{error}</div>}

      {!items && !error && <div className="center-pad muted">{t.dlLoading}</div>}

      {items && !error && filtered.length === 0 && (
        <div className="center-pad muted">
          {t.dlNone(availability?.deploymentMode ?? "—")}
        </div>
      )}

      {grouped.map(([category, list]) => (
        <section key={category} className="dl-section">
          <h3 className="dl-section-title">{categoryLabel(t, category)}</h3>
          <div className="dl-grid">
            {list.map((item) => (
              <DownloadCard key={item.id || item.filename} item={item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
